const SQFT_PER_ACRE = 43560;
const ACRE_DISPLAY_FLOOR = 0.25;
const FOR_SALE_STATUSES = ["active", "comingsoon"];

const positive = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const firstPositive = (...values) => {
  for (const value of values) {
    const number = positive(value);
    if (number) return number;
  }
  return null;
};

const trimZeros = (text) => (text.includes(".") ? text.replace(/\.?0+$/, "") : text);

export const formatLotSize = ({ acres, sqft } = {}) => {
  const knownAcres = positive(acres);
  const knownSqft = positive(sqft);
  const inAcres = knownAcres ?? (knownSqft ? knownSqft / SQFT_PER_ACRE : null);
  if (!inAcres) return "";
  if (inAcres >= ACRE_DISPLAY_FLOOR) {
    return `${Number(inAcres.toFixed(2)).toLocaleString("en-US", {
      maximumFractionDigits: 2,
    })} Acres`;
  }
  const inSqft = knownSqft ?? Math.round(inAcres * SQFT_PER_ACRE);
  return `${Math.round(inSqft).toLocaleString("en-US")} Sqft`;
};

// MLS-sourced rows have no inventory row. The list queries do not select
// mlsListingKey, so a missing Inv_Id is the signal that always holds.
const isMlsIntroduction = (details) => !details?.Inv_Id;

// Older land rows only carry LotSz (acres), with no TotalAcre or unit.
const landLotSize = (details) => {
  if (!positive(details.TotalAcre)) return formatLotSize({ acres: details.LotSz });
  return Number(details.Acre_Sqft) === 2
    ? formatLotSize({ sqft: details.TotalAcre })
    : formatLotSize({ acres: details.TotalAcre });
};

// The feeds store both "ComingSoon" and "Coming Soon".
const statusKey = (status) => status.toLowerCase().replace(/[\s_-]+/g, "");

// Statuses that read naturally after "This home is now". Anything else the
// feeds send (Delete, Incomplete, ...) must not be printed raw.
const NOT_FOR_SALE_LABEL = {
  activeundercontract: "Active Under Contract",
  pending: "Pending",
  closed: "Closed",
  expired: "Expired",
  withdrawn: "Withdrawn",
  canceled: "Canceled",
  cancelled: "Canceled",
  hold: "On Hold",
};

export const introLotSize = (details, { isLand = false } = {}) => {
  if (!details) return "";
  if (isMlsIntroduction(details)) {
    // For MLS rows the API puts the square footage in Acre_Sqft.
    return formatLotSize({
      acres: firstPositive(details.ListingLotSizeAcres, details.TotalAcre),
      sqft: firstPositive(
        details.ListingLotSizeSquareFeet,
        details.LotSz,
        details.Acre_Sqft,
      ),
    });
  }
  if (isLand) return landLotSize(details);
  const lotAcres = firstPositive(details.LotSz, details.lotSize);
  return lotAcres
    ? formatLotSize({ acres: lotAcres })
    : landLotSize(details);
};

export const listingStatusNotice = (details) => {
  const status = String(details?.ListingStatus || "").trim();
  if (!status || !isMlsIntroduction(details)) return "";
  const key = statusKey(status);
  if (FOR_SALE_STATUSES.includes(key)) return "";
  const label = NOT_FOR_SALE_LABEL[key];
  return label ? `This home is now ${label}` : "This home is no longer for sale";
};

export const introPrices = (details) => {
  const introduced = positive(details?.AskingPrice);
  const listed = isMlsIntroduction(details) ? positive(details?.ListingListPrice) : null;
  if (!listed || !introduced || Math.round(listed) === Math.round(introduced)) {
    return { price: introduced ?? listed, introducedPrice: null };
  }
  return { price: listed, introducedPrice: introduced };
};

export const positiveCount = (value) => {
  const number = positive(value);
  return number ? trimZeros(String(number)) : "";
};
